/**
 * ThisWeek Tab Mockup - UX/UI Audit Enhancement Preview
 *
 * This is a MOCKUP showing proposed improvements to the ThisWeek tab.
 * It uses sample data and is completely self-contained.
 *
 * PROPOSED CHANGES (marked with "ENHANCED"):
 * 1. Sticky Section Navigation - Jump between sections quickly
 * 2. Collapsible Sections - Reduce scroll fatigue
 * 3. Today's Urgency Visual Redesign - More prominent urgency indicators
 * 4. Known Bug Indicator - 7-day window should be 5-day (BUG-001)
 * 5. Weekly Trend Mini-Chart - Daily activity visualization (NEW!)
 * 6. Merged Conversions & WHY - Combined into single section
 *
 * KNOWN BUG (to fix in implementation):
 * BUG-001: Activity Monitor uses 7-day window instead of 5-day business days
 *
 * Created: December 3, 2025 for UX/UI Audit
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList
} from 'recharts';

// ============================================
// SAMPLE DATA - Mimics real data patterns
// ============================================
const CURRENT_DAY = 'Tuesday';
const CURRENT_TIME = '2:34 PM';
const HOURS_LEFT = 4;

const WEEK_DATES = {
  monday: { date: '12/2', name: 'Monday', shortName: 'Mon' },
  tuesday: { date: '12/3', name: 'Tuesday', shortName: 'Tue', isToday: true },
  wednesday: { date: '12/4', name: 'Wednesday', shortName: 'Wed', isFuture: true },
  thursday: { date: '12/5', name: 'Thursday', shortName: 'Thu', isFuture: true },
  friday: { date: '12/6', name: 'Friday', shortName: 'Fri', isFuture: true }
};

const SAMPLE_WEEKLY_METRICS = {
  totals: { calls: 145, sms: 82, apps: 5, approved: 3, funded: 2, revenue: 72000, connected: 42, activityCount: 195 },
  weeklyTargets: { calls: 275, sms: 150, apps: 10, revenue: 115000 },
  dailyTargets: { calls: 55, sms: 30, apps: 2, activityCount: 75 },
  today: { calls: 32, sms: 18, apps: 1 },
  todayNeeded: { calls: 23, sms: 12, apps: 1 },
  gaps: { calls: -130, sms: -68, apps: -5 },
  conversions: { callsToApps: 3.4, smsToApps: 6.1, appsToApproved: 60.0, approvedToFunded: 66.7, appsToFunded: 40.0 }
};

const SAMPLE_DAILY_DATA = [
  { day: 'Mon', date: '12/2', calls: 58, sms: 32, connected: 18, apps: 2, approved: 1, funded: 1, revenue: 38000, activityCount: 78, status: 'good' },
  { day: 'Tue', date: '12/3', calls: 32, sms: 18, connected: 12, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 42, status: 'behind', isToday: true },
  { day: 'Wed', date: '12/4', calls: '-', sms: '-', connected: '-', apps: '-', approved: '-', funded: '-', revenue: '-', activityCount: '-', isFuture: true },
  { day: 'Thu', date: '12/5', calls: '-', sms: '-', connected: '-', apps: '-', approved: '-', funded: '-', revenue: '-', activityCount: '-', isFuture: true },
  { day: 'Fri', date: '12/6', calls: '-', sms: '-', connected: '-', apps: '-', approved: '-', funded: '-', revenue: '-', activityCount: '-', isFuture: true }
];

const SAMPLE_REP_TODAY = [
  { rep: 'John', todayCalls: 12, dailyCallTarget: 14, callsNeeded: 2, todaySMS: 8, dailySMSTarget: 8, smsNeeded: 0, todayApps: 1, dailyAppTarget: 1, appsNeeded: 0, avgPct: 95, isOnTrack: false },
  { rep: 'Sarah', todayCalls: 10, dailyCallTarget: 14, callsNeeded: 4, todaySMS: 5, dailySMSTarget: 8, smsNeeded: 3, todayApps: 0, dailyAppTarget: 1, appsNeeded: 1, avgPct: 62, isOnTrack: false },
  { rep: 'Mike', todayCalls: 8, dailyCallTarget: 14, callsNeeded: 6, todaySMS: 4, dailySMSTarget: 8, smsNeeded: 4, todayApps: 0, dailyAppTarget: 1, appsNeeded: 1, avgPct: 48, isOnTrack: false },
  { rep: 'Jane', todayCalls: 2, dailyCallTarget: 14, callsNeeded: 12, todaySMS: 1, dailySMSTarget: 8, smsNeeded: 7, todayApps: 0, dailyAppTarget: 1, appsNeeded: 1, avgPct: 14, isOnTrack: false }
];

// Trend data for mini-chart (completed days only)
const SAMPLE_TREND_DATA = [
  { day: 'Mon', calls: 58, sms: 32, target: 55 },
  { day: 'Tue', calls: 32, sms: 18, target: 55, isToday: true }
];

// Per-rep data for daily breakdown expandable rows - ALL columns to match parent table
const SAMPLE_REP_DAILY_DATA = {
  'Mon': [
    { rep: 'John', calls: 18, sms: 10, connected: 6, apps: 1, approved: 1, funded: 1, revenue: 38000, activityCount: 24, status: 'good' },
    { rep: 'Sarah', calls: 16, sms: 9, connected: 5, apps: 1, approved: 0, funded: 0, revenue: 0, activityCount: 21, status: 'good' },
    { rep: 'Mike', calls: 14, sms: 8, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 18, status: 'ok' },
    { rep: 'Jane', calls: 10, sms: 5, connected: 3, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 15, status: 'behind' }
  ],
  'Tue': [
    { rep: 'John', calls: 12, sms: 8, connected: 5, apps: 1, approved: 1, funded: 0, revenue: 0, activityCount: 17, status: 'good' },
    { rep: 'Sarah', calls: 10, sms: 5, connected: 4, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 13, status: 'ok' },
    { rep: 'Mike', calls: 8, sms: 4, connected: 2, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 10, status: 'behind' },
    { rep: 'Jane', calls: 2, sms: 1, connected: 1, apps: 0, approved: 0, funded: 0, revenue: 0, activityCount: 2, status: 'behind' }
  ]
};

// ============================================
// MAIN MOCKUP COMPONENT
// ============================================
export default function ThisWeekTabMockup() {
  const [activeSection, setActiveSection] = useState('summary');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [expandedDay, setExpandedDay] = useState(null);

  // Set unique browser tab title
  useEffect(() => {
    document.title = '🎨 MOCKUP: ThisWeek Tab';
  }, []);

  // Refs for scrolling
  const sectionRefs = {
    summary: useRef(null),
    urgency: useRef(null),
    daily: useRef(null),
    why: useRef(null)
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const formatCurrency = (value) => {
    if (value === '-') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="dashboard-header">
        <h1>Sales Performance Dashboard</h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fef3c7',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '2px solid #f59e0b'
        }}>
          <span style={{ fontSize: '18px' }}>🎨</span>
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            MOCKUP MODE - Proposed ThisWeek Tab Enhancements | 🔧 P2 FIX: box-shadow standardized
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button>Overview</button>
        <button>This Month</button>
        <button className="active">This Week</button>
        <button>Last Week</button>
      </nav>

      {/* ============================================ */}
      {/* KNOWN BUG BANNER                            */}
      {/* ============================================ */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '2px solid #ef4444',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>🐛</span>
        <div>
          <strong style={{ color: '#991b1b' }}>BUG-001 (To Fix in Implementation):</strong>
          <span style={{ color: '#7f1d1d', marginLeft: '8px' }}>
            Activity Monitor uses 7-day window instead of 5-day business days (Mon-Fri only)
          </span>
        </div>
      </div>

      {/* ============================================ */}
      {/* ENHANCED: Sticky Section Navigation         */}
      {/* ============================================ */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 0',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'summary', label: '📊 Week Summary', icon: '1' },
            { id: 'urgency', label: '🚨 Today\'s Urgency', icon: '2' },
            { id: 'daily', label: '📅 Daily Breakdown', icon: '3' },
            { id: 'why', label: '🔍 WHY Analysis', icon: '4' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === section.id ? '#eff6ff' : 'white',
                color: activeSection === section.id ? '#1e40af' : '#475569',
                fontWeight: activeSection === section.id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {section.label}
            </button>
          ))}

          <div style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>
            ✨ NEW: Sticky section navigation
          </div>
        </div>
      </div>

      <main className="dashboard-content">
        <div className="this-week-tab">
          <h2>📅 This Week - Team's Activity Monitor</h2>
          <p className="tab-description">
            Week of Dec 2 - Dec 8 • Today is {CURRENT_DAY} at {CURRENT_TIME}
          </p>

          {/* ============================================ */}
          {/* SECTION 1: WEEK SUMMARY                     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.summary}
            style={{
              scrollMarginTop: '80px',
              marginTop: '24px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('summary')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Week at a Glance</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  High-level weekly performance summary
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.summary ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.summary && (
              <>
                {/* Summary Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <SummaryCard
                    icon="📞"
                    title="Calls This Week"
                    value={`${SAMPLE_WEEKLY_METRICS.totals.calls} / ${SAMPLE_WEEKLY_METRICS.weeklyTargets.calls}`}
                    status={SAMPLE_WEEKLY_METRICS.gaps.calls >= 0 ? 'on-track' : 'behind'}
                    gap={Math.abs(SAMPLE_WEEKLY_METRICS.gaps.calls)}
                  />
                  <SummaryCard
                    icon="💬"
                    title="SMS This Week"
                    value={`${SAMPLE_WEEKLY_METRICS.totals.sms} / ${SAMPLE_WEEKLY_METRICS.weeklyTargets.sms}`}
                    status={SAMPLE_WEEKLY_METRICS.gaps.sms >= 0 ? 'on-track' : 'behind'}
                    gap={Math.abs(SAMPLE_WEEKLY_METRICS.gaps.sms)}
                  />
                  <SummaryCard
                    icon="📋"
                    title="Apps This Week"
                    value={`${SAMPLE_WEEKLY_METRICS.totals.apps} / ${SAMPLE_WEEKLY_METRICS.weeklyTargets.apps}`}
                    status={SAMPLE_WEEKLY_METRICS.gaps.apps >= 0 ? 'on-track' : 'behind'}
                    gap={Math.abs(SAMPLE_WEEKLY_METRICS.gaps.apps)}
                  />
                  <SummaryCard
                    icon="📈"
                    title="Call → App Rate"
                    value={formatPercent(SAMPLE_WEEKLY_METRICS.conversions.callsToApps)}
                    status="neutral"
                  />
                </div>

                {/* ENHANCED: Weekly Trend Mini-Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '24px',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>📈 Daily Activity So Far</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#065f46',
                        fontWeight: 600
                      }}>
                        ✨ NEW: Trend visualization
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: '#1e40af',
                        fontWeight: 600
                      }}>
                        🏷️ 12/5 LABELS
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={SAMPLE_TREND_DATA}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="calls" position="top" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} />
                      </Bar>
                      <Bar dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="sms" position="top" style={{ fontSize: 11, fill: '#9333ea', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#92400e'
                  }}>
                    ⚠️ <strong>Today (Tue)</strong> is behind target - only 32 calls vs 55 daily target. Need {55 - 32} more calls.
                  </div>
                </div>

                {/* Conversion Rates - Now combined with WHY Analysis below */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>📈 Conversion Rates</h3>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#d1fae5',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#065f46',
                      fontWeight: 600
                    }}>
                      ✨ MERGED: Was separate from WHY
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '12px'
                  }}>
                    <ConversionCard label="Call → App" rate={SAMPLE_WEEKLY_METRICS.conversions.callsToApps} detail={`${SAMPLE_WEEKLY_METRICS.totals.apps} apps from ${SAMPLE_WEEKLY_METRICS.totals.calls} calls`} />
                    <ConversionCard label="SMS → App" rate={SAMPLE_WEEKLY_METRICS.conversions.smsToApps} detail={`${SAMPLE_WEEKLY_METRICS.totals.apps} apps from ${SAMPLE_WEEKLY_METRICS.totals.sms} SMS`} />
                    <ConversionCard label="App → Approved" rate={SAMPLE_WEEKLY_METRICS.conversions.appsToApproved} detail={`${SAMPLE_WEEKLY_METRICS.totals.approved} from ${SAMPLE_WEEKLY_METRICS.totals.apps} apps`} />
                    <ConversionCard label="Approved → Funded" rate={SAMPLE_WEEKLY_METRICS.conversions.approvedToFunded} detail={`${SAMPLE_WEEKLY_METRICS.totals.funded} from ${SAMPLE_WEEKLY_METRICS.totals.approved} approved`} />
                    <ConversionCard label="App → Funded" rate={SAMPLE_WEEKLY_METRICS.conversions.appsToFunded} detail={`${SAMPLE_WEEKLY_METRICS.totals.funded} from ${SAMPLE_WEEKLY_METRICS.totals.apps} apps`} />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 2: TODAY'S URGENCY                  */}
          {/* ENHANCED: More prominent visual design      */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.urgency}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('urgency')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🚨 {CURRENT_DAY.toUpperCase()} - WHAT NEEDS TO HAPPEN TODAY</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Current Time: {CURRENT_TIME} | Hours Left: ~{HOURS_LEFT} hours
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.urgency ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.urgency && (
              <>
                {/* ENHANCED: Redesigned urgency cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <UrgencyCard
                    icon="📞"
                    label="CALLS"
                    done={SAMPLE_WEEKLY_METRICS.today.calls}
                    needed={SAMPLE_WEEKLY_METRICS.todayNeeded.calls}
                    perHour={Math.ceil(SAMPLE_WEEKLY_METRICS.todayNeeded.calls / HOURS_LEFT)}
                    hoursLeft={HOURS_LEFT}
                  />
                  <UrgencyCard
                    icon="💬"
                    label="SMS"
                    done={SAMPLE_WEEKLY_METRICS.today.sms}
                    needed={SAMPLE_WEEKLY_METRICS.todayNeeded.sms}
                    perHour={Math.ceil(SAMPLE_WEEKLY_METRICS.todayNeeded.sms / HOURS_LEFT)}
                    hoursLeft={HOURS_LEFT}
                  />
                  <UrgencyCard
                    icon="📋"
                    label="APPS"
                    done={SAMPLE_WEEKLY_METRICS.today.apps}
                    needed={SAMPLE_WEEKLY_METRICS.todayNeeded.apps}
                    action="Focus on warm leads"
                  />
                </div>

                {/* Enhancement indicator */}
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <span>✨</span>
                  <strong>ENHANCED:</strong> Redesigned urgency cards with larger, more prominent metrics
                </div>

                {/* Per-Rep Today Status - Clean design with border accent */}
                <div style={{
                  marginTop: '16px',
                  background: 'white',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #e2e8f0',
                  borderLeft: '4px solid #3b82f6',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '8px'
                  }}>
                    📋 Per-Rep Today Status
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#1e293b', fontWeight: '600' }}>Rep</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Calls</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Need</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>SMS</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Need</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Apps</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Need</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SAMPLE_REP_TODAY.map(rep => (
                          <tr key={rep.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontWeight: '600' }}>{rep.rep}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{rep.todayCalls}/{rep.dailyCallTarget}</td>
                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: rep.callsNeeded === 0 ? '#48bb78' : '#fbbf24' }}>
                              {rep.callsNeeded === 0 ? '✓' : rep.callsNeeded}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{rep.todaySMS}/{rep.dailySMSTarget}</td>
                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: rep.smsNeeded === 0 ? '#48bb78' : '#fbbf24' }}>
                              {rep.smsNeeded === 0 ? '✓' : rep.smsNeeded}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{rep.todayApps}/{rep.dailyAppTarget}</td>
                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: rep.appsNeeded === 0 ? '#48bb78' : '#fbbf24' }}>
                              {rep.appsNeeded === 0 ? '✓' : rep.appsNeeded}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: rep.isOnTrack ? '#48bb78' : '#fbbf24' }}>
                              {rep.isOnTrack ? '✅ On Track' : `${rep.avgPct}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 3: DAILY BREAKDOWN                  */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.daily}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('daily')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Daily Breakdown</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Day-by-day performance with rep drill-down (click rows to expand)
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.daily ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.daily && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Day</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Calls</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>SMS</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Connected</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Apps</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Approved</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Funded</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Revenue</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Activity</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SAMPLE_DAILY_DATA.map((day, idx) => (
                      <React.Fragment key={day.day}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e2e8f0',
                            backgroundColor: day.isToday ? '#eff6ff' : day.isFuture ? '#f9fafb' : 'white',
                            cursor: !day.isFuture ? 'pointer' : 'default'
                          }}
                          onClick={() => !day.isFuture && setExpandedDay(expandedDay === day.day ? null : day.day)}
                        >
                          <td style={{ padding: '12px', fontWeight: 600 }}>
                            {day.day} <span style={{ color: '#64748b', fontWeight: 400 }}>{day.date}</span>
                            {day.isToday && <span style={{ marginLeft: '8px', color: '#3b82f6' }}>(Today)</span>}
                            {!day.isFuture && <span style={{ marginLeft: '8px', color: '#94a3b8' }}>{expandedDay === day.day ? '▼' : '▶'}</span>}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.calls}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.sms}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.connected}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.apps}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.approved}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.funded}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{day.revenue === '-' ? '-' : formatCurrency(day.revenue)}</td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: 600,
                            backgroundColor: day.isFuture ? 'transparent' :
                              day.activityCount >= SAMPLE_WEEKLY_METRICS.dailyTargets.activityCount ? '#d1fae5' :
                              day.activityCount >= SAMPLE_WEEKLY_METRICS.dailyTargets.activityCount * 0.8 ? '#fef3c7' : '#fee2e2'
                          }}>
                            {day.activityCount === '-' ? '-' : `${day.activityCount}/${SAMPLE_WEEKLY_METRICS.dailyTargets.activityCount}`}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {day.isFuture ? '⏳ Future' :
                             day.status === 'good' ? '✅ Good' :
                             day.status === 'behind' ? '🔴 Behind' : '⚠️ Slow'}
                          </td>
                        </tr>
                        {/* Expandable Per-Rep Breakdown */}
                        {expandedDay === day.day && !day.isFuture && SAMPLE_REP_DAILY_DATA[day.day] && (
                          <tr>
                            <td colSpan="10" style={{ padding: '0', backgroundColor: '#f8fafc' }}>
                              <div style={{
                                padding: '16px 24px',
                                borderLeft: '4px solid #3b82f6'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>👥 Per-Rep Breakdown for {day.day}</span>
                                  <span style={{
                                    padding: '2px 8px',
                                    backgroundColor: '#d1fae5',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    color: '#065f46',
                                    fontWeight: 600
                                  }}>
                                    ✨ NEW
                                  </span>
                                </div>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Rep</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Calls</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>SMS</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Connected</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Apps</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Approved</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Funded</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Revenue</th>
                                      <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Activity</th>
                                      <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {SAMPLE_REP_DAILY_DATA[day.day].map(rep => (
                                      <tr key={rep.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '8px', fontWeight: 500 }}>{rep.rep}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.calls}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.sms}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.connected}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.apps}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.approved}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.funded}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.revenue > 0 ? formatCurrency(rep.revenue) : '-'}</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{rep.activityCount}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                          <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            backgroundColor: rep.status === 'good' ? '#d1fae5' : rep.status === 'ok' ? '#fef3c7' : '#fee2e2',
                                            color: rep.status === 'good' ? '#065f46' : rep.status === 'ok' ? '#92400e' : '#991b1b'
                                          }}>
                                            {rep.status === 'good' ? '✅ Good' : rep.status === 'ok' ? '⚠️ OK' : '🔴 Behind'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Total Row */}
                    <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '12px' }}>TOTAL</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.calls}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.sms}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.connected}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.apps}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.approved}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.funded}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(SAMPLE_WEEKLY_METRICS.totals.revenue)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.totals.activityCount}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>-</td>
                    </tr>
                    {/* Target Row */}
                    <tr style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
                      <td style={{ padding: '12px' }}>TARGET</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.weeklyTargets.calls}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.weeklyTargets.sms}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.weeklyTargets.apps}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(SAMPLE_WEEKLY_METRICS.weeklyTargets.revenue)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_WEEKLY_METRICS.dailyTargets.activityCount} (daily)</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 4: WHY ANALYSIS                     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.why}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('why')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🔍 WHY Analysis</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Activity and efficiency metrics driving this week's performance
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.why ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.why && (
              <>
                <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>Activity</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <MetricCard label="Total Calls" value={SAMPLE_WEEKLY_METRICS.totals.calls} detail="This week so far" />
                  <MetricCard label="Total SMS" value={SAMPLE_WEEKLY_METRICS.totals.sms} detail="This week so far" />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>Efficiency Metrics</h3>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '10px',
                    color: '#92400e',
                    fontWeight: 600,
                    border: '1px solid #f59e0b'
                  }}>
                    🔍 DATA REVIEW: Needs pack_source data
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px'
                }}>
                  <MetricCard
                    label="Avg Deal Size"
                    value={formatCurrency(SAMPLE_WEEKLY_METRICS.totals.revenue / SAMPLE_WEEKLY_METRICS.totals.funded)}
                    detail={`${formatCurrency(SAMPLE_WEEKLY_METRICS.totals.revenue)} ÷ ${SAMPLE_WEEKLY_METRICS.totals.funded} deals`}
                  />
                  <MetricCard
                    label="Calls per App"
                    value={(SAMPLE_WEEKLY_METRICS.totals.calls / SAMPLE_WEEKLY_METRICS.totals.apps).toFixed(1)}
                    detail={`${SAMPLE_WEEKLY_METRICS.totals.calls} calls ÷ ${SAMPLE_WEEKLY_METRICS.totals.apps} apps`}
                  />
                  <MetricCard
                    label="SMS per App"
                    value={(SAMPLE_WEEKLY_METRICS.totals.sms / SAMPLE_WEEKLY_METRICS.totals.apps).toFixed(1)}
                    detail={`${SAMPLE_WEEKLY_METRICS.totals.sms} SMS ÷ ${SAMPLE_WEEKLY_METRICS.totals.apps} apps`}
                  />
                  <MetricCard
                    label="Apps per Funded"
                    value={(SAMPLE_WEEKLY_METRICS.totals.apps / SAMPLE_WEEKLY_METRICS.totals.funded).toFixed(1)}
                    detail={`${SAMPLE_WEEKLY_METRICS.totals.apps} apps ÷ ${SAMPLE_WEEKLY_METRICS.totals.funded} funded`}
                  />
                </div>
              </>
            )}
          </section>

        </div>
      </main>

      {/* Enhancement Legend */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e40af' }}>
          Enhancements in This Mockup:
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <EnhancementItem
            title="1. Sticky Section Navigation"
            description="Jump between sections quickly without scrolling."
          />
          <EnhancementItem
            title="2. Collapsible Sections"
            description="Click section headers to collapse/expand."
          />
          <EnhancementItem
            title="3. Redesigned Urgency Cards"
            description="Larger, more prominent metrics with per-hour breakdown."
          />
          <EnhancementItem
            title="4. Known Bug Banner"
            description="BUG-001: 7-day window should be 5-day business days."
          />
          <EnhancementItem
            title="5. Weekly Trend Mini-Chart"
            description="NEW: Visualize daily activity trends (Mon-Tue so far)."
          />
          <EnhancementItem
            title="6. Merged Conversions & WHY"
            description="Conversion rates now shown with efficiency metrics."
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function SummaryCard({ icon, title, value, status, gap }) {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{title}</h4>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{value}</div>
      <div style={{
        fontSize: '12px',
        fontWeight: 600,
        color: status === 'on-track' ? '#10b981' : status === 'behind' ? '#ef4444' : '#64748b'
      }}>
        {status === 'on-track' ? '✅ On Track' : status === 'behind' ? `⚠️ ${gap} behind` : 'Calls → Apps'}
      </div>
    </div>
  );
}

function ConversionCard({ label, rate, detail }) {
  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{detail}</div>
    </div>
  );
}

function UrgencyCard({ icon, label, done, needed, perHour, hoursLeft, action }) {
  const isComplete = needed === 0;
  // Option C: Clean white background with colored left border accent
  const borderColor = isComplete ? '#10b981' : needed > 5 ? '#ef4444' : '#f59e0b'; // green/red/amber

  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      borderLeft: `4px solid ${borderColor}`,
      textAlign: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>{label}</div>
      <div style={{ fontSize: '36px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
        {done}
      </div>
      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
        done, need <strong style={{ color: borderColor }}>{needed}</strong> more
      </div>
      {perHour && needed > 0 && (
        <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
          → {perHour} per hour for {hoursLeft}h
        </div>
      )}
      {action && needed > 0 && (
        <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
          → {action}
        </div>
      )}
      {isComplete && (
        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
          ✓ Target met!
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, detail }) {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{detail}</div>
    </div>
  );
}

function EnhancementItem({ title, description }) {
  return (
    <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
      <strong style={{ color: '#10b981' }}>{title}</strong>
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>{description}</p>
    </div>
  );
}
